/**
 * Weather File Index
 *
 * AUTO-GENERATED FILE - Do not edit directly!
 * Generated by: npx tsx scripts/generate-weather-index.ts
 * Generated at: 2026-01-17T10:36:27.444Z
 *
 * Weather file data is loaded from JSON files at runtime.
 * JSON files are in: public/weather-data/
 *
 * Source: https://climate.onebuilding.org
 * License: CC BY 4.0
 */

import type { WeatherFileMetadata } from '../types';

export interface WeatherIndexManifest {
  generatedAt: string;
  totalEntries: number;
  source: string;
  license: string;
  countries: Array<{ code: string; name: string; count: number }>;
  climateZones: Array<{ zone: string; count: number }>;
  sourceTypes: Array<{ type: string; count: number }>;
  yearRanges: Array<{ yearRange: string; count: number }>;
}

// Cache for loaded weather data
let manifestCache: WeatherIndexManifest | null = null;
const countryCache = new Map<string, WeatherFileMetadata[]>();

/**
 * Load the weather index manifest (list of countries and stats).
 */
export async function loadWeatherManifest(): Promise<WeatherIndexManifest> {
  if (manifestCache) return manifestCache;

  const response = await fetch('/weather-data/manifest.json');
  if (!response.ok) {
    throw new Error(`Failed to load weather manifest: ${response.status}`);
  }
  manifestCache = await response.json();
  return manifestCache!;
}

/**
 * Load weather files for a specific country.
 */
export async function loadCountryWeatherFiles(countryCode: string): Promise<WeatherFileMetadata[]> {
  const code = countryCode.toLowerCase();
  if (countryCache.has(code)) {
    return countryCache.get(code)!;
  }

  const response = await fetch(`/weather-data/${code}.json`);
  if (!response.ok) {
    if (response.status === 404) {
      return []; // Country not found
    }
    throw new Error(`Failed to load weather files for ${countryCode}: ${response.status}`);
  }

  const data: WeatherFileMetadata[] = await response.json();
  countryCache.set(code, data);
  return data;
}

/**
 * Load all weather files (use sparingly - this loads all data).
 */
export async function loadAllWeatherFiles(): Promise<WeatherFileMetadata[]> {
  const manifest = await loadWeatherManifest();
  const results: WeatherFileMetadata[] = [];

  for (const country of manifest.countries) {
    const files = await loadCountryWeatherFiles(country.code);
    results.push(...files);
  }

  return results;
}

/**
 * Clear the weather data cache.
 */
export function clearWeatherCache(): void {
  manifestCache = null;
  countryCache.clear();
}

// For backwards compatibility - empty array, use async loaders instead
export const WEATHER_FILE_INDEX: WeatherFileMetadata[] = [];

export default WEATHER_FILE_INDEX;

// ============ Tiered Data Loaders ============
// These load extended data generated by extract-weather-data.ts

import type {
  MonthlyClimateEntry,
  ClimateCountryIndex,
  DesignConditionsSummary,
  DesignCountryIndex,
  DdyCountryIndex,
  DesignDayMetadata,
} from '../types';

// Cache for tiered data
const climateCache = new Map<string, ClimateCountryIndex>();
const designCache = new Map<string, DesignCountryIndex>();
const ddyCache = new Map<string, DdyCountryIndex>();

/**
 * Load monthly climate data for a country's weather files.
 * Returns null if data hasn't been extracted yet.
 */
export async function loadCountryClimateData(countryCode: string): Promise<ClimateCountryIndex | null> {
  const code = countryCode.toLowerCase();
  if (climateCache.has(code)) {
    return climateCache.get(code)!;
  }

  try {
    const response = await fetch(`/weather-data/climate/${code}.json`);
    if (!response.ok) {
      return null; // Data not yet extracted
    }
    const data: ClimateCountryIndex = await response.json();
    climateCache.set(code, data);
    return data;
  } catch {
    return null;
  }
}

/**
 * Load design conditions summary for a country's weather files.
 * Returns null if data hasn't been extracted yet.
 */
export async function loadCountryDesignData(countryCode: string): Promise<DesignCountryIndex | null> {
  const code = countryCode.toLowerCase();
  if (designCache.has(code)) {
    return designCache.get(code)!;
  }

  try {
    const response = await fetch(`/weather-data/design/${code}.json`);
    if (!response.ok) {
      return null; // Data not yet extracted
    }
    const data: DesignCountryIndex = await response.json();
    designCache.set(code, data);
    return data;
  } catch {
    return null;
  }
}

/**
 * Load DDY design day metadata index for a country.
 * Returns null if data hasn't been extracted yet.
 */
export async function loadCountryDdyIndex(countryCode: string): Promise<DdyCountryIndex | null> {
  const code = countryCode.toLowerCase();
  if (ddyCache.has(code)) {
    return ddyCache.get(code)!;
  }

  try {
    const response = await fetch(`/weather-data/ddy/${code}.json`);
    if (!response.ok) {
      return null; // Data not yet extracted
    }
    const data: DdyCountryIndex = await response.json();
    ddyCache.set(code, data);
    return data;
  } catch {
    return null;
  }
}

/**
 * Get monthly climate data for a specific weather file.
 * Returns null if data hasn't been extracted yet.
 */
export async function getFileClimateData(
  fileId: string,
  countryCode: string
): Promise<MonthlyClimateEntry[] | null> {
  const countryData = await loadCountryClimateData(countryCode);
  if (!countryData) return null;
  return countryData[fileId] ?? null;
}

/**
 * Get design conditions summary for a specific weather file.
 * Returns null if data hasn't been extracted yet.
 */
export async function getFileDesignData(
  fileId: string,
  countryCode: string
): Promise<DesignConditionsSummary | null> {
  const countryData = await loadCountryDesignData(countryCode);
  if (!countryData) return null;
  return countryData[fileId] ?? null;
}

/**
 * Get design day metadata for a specific weather file.
 * Returns null if data hasn't been extracted yet.
 */
export async function getFileDdyMetadata(
  fileId: string,
  countryCode: string
): Promise<DesignDayMetadata[] | null> {
  const countryData = await loadCountryDdyIndex(countryCode);
  if (!countryData) return null;
  const entry = countryData[fileId];
  return entry?.designDays ?? null;
}

/**
 * Clear all tiered data caches.
 */
export function clearTieredDataCache(): void {
  climateCache.clear();
  designCache.clear();
  ddyCache.clear();
}
